import React, { ReactNode } from 'react';
import { CSSObject, Global } from '@emotion/core';
import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'framer-motion';

import useBreakpoints, { isScreen } from '@shared/v2/hooks/useBreakpoints';

import { SCREEN_SIZE } from '../../constants';
import Portal from '../Portal';
import { AnimationGroup } from './animations';
import { Backdrop } from './components';

export type ModalProps = {
  isOpen?: boolean;
  children?: ReactNode;
  onClose?: () => void;
  className?: string;
  animation?: AnimationGroup;
};

const BodyStyle = `
  width: 100%;
  background: white;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.12);
  
  ${SCREEN_SIZE.LG} {
    display: flex;
    width: auto;
  }
`;
const ModalBodyAnimated = styled(motion.div)`
  ${BodyStyle}
`;

const ModalBody = styled.div`
  ${BodyStyle}
`;

const globalStyles: CSSObject = {
  body: {
    overflowY: 'hidden',
  },
};

const Modal = ({
  isOpen,
  children,
  onClose,
  className,
  animation,
}: ModalProps) => {
  const breakpoints = useBreakpoints();

  if (!!animation) {
    const smallScreen = isScreen(breakpoints)('xs', 'sm');
    const variant = Array.isArray(animation)
      ? smallScreen
        ? animation[0]
        : animation[2]
      : animation;

    return (
      <AnimatePresence initial={false}>
        {isOpen && (
          <>
            <Global styles={globalStyles} />
            <Portal selector="portal-root">
              <Backdrop onClick={onClose}>
                <ModalBodyAnimated
                  className={className ?? ''}
                  onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
                    e.stopPropagation()
                  }
                  variants={variant}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  {children}
                </ModalBodyAnimated>
              </Backdrop>
            </Portal>
          </>
        )}
      </AnimatePresence>
    );
  }

  return (
    isOpen && (
      <>
        <Global styles={globalStyles} />
        <Portal selector="portal-root">
          <Backdrop onClick={onClose}>
            <ModalBody className={className ?? ''}>{children}</ModalBody>
          </Backdrop>
        </Portal>
      </>
    )
  );
};

export default Modal;
